import {
  formatEther,
  formatUnits,
  getPublicClient,
  getWalletAccount,
  getWalletClient,
  parseUnits,
  readContract,
  waitForBlocks,
  waitForConfirmation,
  writeContractAndWait,
  zeroAddress,
} from "../viem";
import type { Address, Hex } from "viem";

import ERC20Abi from "../../abis/ERC20.json";
import ERC1155Abi from "../../abis/OGCrate(ERC1155).json";

export { waitForBlocks, waitForConfirmation };

export const getTokenBalance = async (
  tokenAddress: string,
  userAddress: string
) => {
  if (tokenAddress === zeroAddress) {
    const balance = await getPublicClient().getBalance({
      address: userAddress as Address,
    });
    return formatEther(balance);
  }

  const balance = await readContract<string>({
    address: tokenAddress,
    abi: ERC20Abi,
    functionName: "balanceOf",
    args: [userAddress],
  });
  const decimal = await readContract<string>({
    address: tokenAddress,
    abi: ERC20Abi,
    functionName: "decimals",
  });

  return formatUnits(balance, decimal);
};

export const getTokenAllowance = async (
  tokenAddress: string,
  userAddress: string,
  contractAddress: string
) => {
  const allowance = await readContract<string>({
    address: tokenAddress,
    abi: ERC20Abi,
    functionName: "allowance",
    args: [userAddress, contractAddress],
  });
  const decimal = await readContract<string>({
    address: tokenAddress,
    abi: ERC20Abi,
    functionName: "decimals",
  });

  return formatUnits(allowance, decimal);
};

export const approveToken = async (
  tokenAddress: string,
  amount: number,
  to: string,
  account: string
) => {
  const allowanceBN = await readContract<string>({
    address: tokenAddress,
    abi: ERC20Abi,
    functionName: "allowance",
    args: [account, to],
  });
  const decimals = Number(
    await readContract<string>({
      address: tokenAddress,
      abi: ERC20Abi,
      functionName: "decimals",
    })
  );
  const allowance = Number(formatUnits(allowanceBN, decimals));

  if (allowance < amount) {
    await writeContractAndWait({
      address: tokenAddress,
      abi: ERC20Abi,
      functionName: "approve",
      args: [to, parseUnits(amount.toFixed(decimals), decimals)],
      account,
    });
  }
};

export const approveTokenWei = async (
  tokenAddress: string,
  amountWei: string,
  to: string,
  account: string
) => {
  const allowanceBN = await readContract<string>({
    address: tokenAddress,
    abi: ERC20Abi,
    functionName: "allowance",
    args: [account, to],
  });

  if (BigInt(allowanceBN) < BigInt(amountWei)) {
    await writeContractAndWait({
      address: tokenAddress,
      abi: ERC20Abi,
      functionName: "approve",
      args: [to, BigInt(amountWei)],
      account,
    });
  }
};

export const signMessage = async (message: any) => {
  const waitForWallet = () => {
    return new Promise<void>((resolve) => {
      const checkWallet = () => {
        if (window.wallet?.provider) {
          resolve();
        } else {
          setTimeout(checkWallet, 100);
        }
      };
      checkWallet();
    });
  };

  await waitForWallet();

  const account = getWalletAccount();
  const walletClient = getWalletClient(account);

  return walletClient.signMessage({
    account,
    message: JSON.stringify(message),
  });
};

export const transferErc1155 = async (
  from: string,
  to: string,
  amount: number,
  tokenAddress: string
) => {
  const receipt = await writeContractAndWait({
    address: tokenAddress,
    abi: ERC1155Abi,
    functionName: "safeTransferFrom",
    args: [from, to, 0, amount, "0x" as Hex],
    account: from,
  });

  return receipt.blockHash;
};

/**
 * Execute multiple contract calls in parallel using Promise.all
 */
export const executeBatchRequest = async <T = any>(
  requests: Array<() => Promise<T>>
): Promise<T[]> => {
  return Promise.all(requests.map((req) => req()));
};
